import { KeyboardEvent, useCallback, useRef } from 'react'

const FIRST_SLOT_INDEX = 1

export interface DecimalCommaPlacement {
  registerSlot: (index: number, element: HTMLElement | null) => void
  onSlotClick: (index: number) => void
  onSlotKeyDown: (event: KeyboardEvent, index: number) => void
}

export default function useDecimalCommaPlacement({
  slotCount,
  placedIndex,
  onPlacedIndexChange,
}: {
  slotCount: number
  placedIndex?: number
  onPlacedIndexChange: (index?: number) => void
}): DecimalCommaPlacement {
  const slotsRef = useRef(new Map<number, HTMLElement>())

  const registerSlot = useCallback((index: number, element: HTMLElement | null): void => {
    if (element) {
      slotsRef.current.set(index, element)
    } else {
      slotsRef.current.delete(index)
    }
  }, [])

  const onSlotClick = (index: number): void => {
    onPlacedIndexChange(placedIndex === index ? undefined : index)
  }

  const onSlotKeyDown = (event: KeyboardEvent, index: number): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSlotClick(index)
      return
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return

    const target = index + (event.key === 'ArrowLeft' ? -1 : 1)
    if (target < FIRST_SLOT_INDEX || target > slotCount) return

    event.preventDefault()
    slotsRef.current.get(target)?.focus()
    // Keep the chosen sign under the moving focus, so the arrow keys nudge it along the row.
    if (placedIndex === index) onPlacedIndexChange(target)
  }

  return { onSlotClick, onSlotKeyDown, registerSlot }
}
